<template>
	<div class="flex grow xl:max-w-[22rem]">
		<SearchBox
			:focus
			:model-value="search"
			:show-button="showButton"
			:focus-input-border="focusInputBorder"
			@submit-search="submitSearch()"
			@update:model-value="(val: string | string[]) => search = Array.isArray(val) ? val[0] ?? '' : val"
		/>
		<SearchModal v-model:visible="visibleSearchPopup" :is-sticky-header="isStickyHeader">
			<template #searchBox>
				<SearchBox
					:focus
					show-button
					input-border
					:model-value="search"
					@submit-search="submitSearch()"
					@update:model-value="(val: string | string[]) => search = Array.isArray(val) ? val[0] ?? '' : val"
				/>
			</template>
			<SearchWidget
				hide-first-heading
				popup
			/>
		</SearchModal>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {useSearchStore} from '~/stores/search';

	type Props = {
		focus?: boolean;
		isStickyHeader?: boolean;
		showButton?: boolean;
		focusInputBorder?: boolean;
	};

	defineProps<Props>();

	const searchStore = useSearchStore();
	const {visibleSearchPopup, getSearchValue} = storeToRefs(searchStore);

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketWithFallbackInstance} = storeToRefs(hypermarketsStore);

	const route = useRoute();
	const search = ref(String(route.query.search || ''));
	const routeSearch = computed(() => ['branch-vysledky-vyhledavani', 'vysledky-vyhledavani'].includes(String(route.name)));

	if (getSearchValue.value !== null && getSearchValue.value.length > 0) {
		search.value = getSearchValue.value;
	}

	watchDebounced(
		[search],
		async () => {
			if (!routeSearch.value && search.value.length >= 3) {
				setVisibleSearchPopup(true);
			}

			if (search.value.length >= 3) {
				searchStore.setSearchValue(String(search.value));
				await fetchSearch();
			}

			if (!search.value.length) {
				await syncSearchQuery(null);
			}
		},
		{debounce: 1000},
	);

	watch(routeSearch, () => {
		setVisibleSearchPopup(false);
	});

	watch(selectedHypermarket, async (newValue) => {
		if (!routeSearch.value && newValue?.length && search.value.length >= 3 && visibleSearchPopup.value) {
			setVisibleSearchPopup(false);
			await navigateTo({
				name: 'branch-vysledky-vyhledavani',
				params: {branch: selectedHypermarket.value},
				query: {search: search.value},
			});
			await nextTick();
			await fetchSearch();
		}
	});

	async function fetchSearch() {
		await syncSearchQuery(String(search.value));
		const gsoaId = Number(selectedHypermarketWithFallbackInstance.value?.content.gsoaId);
		if (!searchStore.hasSearchData(String(search.value), gsoaId)) {
			await searchStore.fetchSearch({text: String(search.value), gsoaId});
		}
	}

	async function submitSearch() {
		if (search.value.length >= 3) {
			setVisibleSearchPopup(false);
			if (selectedHypermarket.value) {
				await navigateTo({name: 'branch-vysledky-vyhledavani', params: {branch: selectedHypermarketWithFallbackInstance.value?.slug}, query: {search: search.value}});
			} else {
				await navigateTo({name: 'vysledky-vyhledavani', query: {search: search.value}});
			}
			await nextTick();
			await fetchSearch();
		}
	}

	async function syncSearchQuery(value: string | null) {
		const query = {...route.query};
		if (value === null) {
			delete query.search;
		} else {
			query.search = value;
		}

		if ((route.query.search ?? null) === value) {
			return;
		}

		await navigateTo({path: route.path, query, hash: route.hash}, {replace: true});
		await nextTick();
	}

	function setVisibleSearchPopup(state: boolean) {
		searchStore.setVisibleSearchPopup(state);
	}
</script>
